/**
 * The look of the entry point: the icon and the badge of each state, and whether the invitation
 * is offered at all (R46).
 *
 * It sits inside ① **Ponto de entrada**, under the labels, because it describes the same control
 * those labels are on — one button with two faces. It is **not** part of `texts`: an icon variant
 * and a badge enum read the same in every language, so they have one value each and not three.
 * `badgeTooltip` is the exception and stays up with the texts, where it can be translated.
 *
 * The icon is a **picker, never a free field**. The BFF validates the `Namespace.Glyph` shape and
 * nothing else — a well-formed name that does not exist is accepted, and `GLIcon` does not throw
 * on it, it draws the words `Icon not found: Map.Layers` into the customer's sidebar. This screen
 * is the only place that can catch it, so it checks against the real catalogue in `Shared/icons`.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import { useState } from 'react';
import type { JSX } from 'react';
import type { OptInEntryBadge } from '../../../Types/OptInFeatures.types';
import type { OptInAppearanceDraft } from '../../../Utils/optInFeatureForm';
import { OPT_IN_BADGE_CHOICES } from '../../../Utils/optInFeatureForm';
import { IconGlyph, IconPicker } from '@/Shared/components/IconPicker/IconPicker';
import { Switch } from '@/Shared/components/Switch/Switch';

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const headStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const headTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const headHintStyle = css`
  margin: 0;
  flex: 1 1 320px;
  min-width: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-s) var(--space-m);

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

/** The JSON key, in mono, exactly as the text fields above label themselves. */
const monoLabelStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const fieldNameStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

/** The picker is a popover, so its anchor has to establish the positioning context. */
const anchorStyle = css`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  min-width: 0;
`;

const triggerStyle = css`
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  flex: 1 1 auto;
  min-width: 0;
  height: 36px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: var(--color-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 1px;
  }
`;

const triggerTextStyle = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const inheritedStyle = css`
  color: var(--color-text-muted);
`;

const clearStyle = css`
  flex: 0 0 auto;
  height: 36px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid transparent;
  background: transparent;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-2xs);
  cursor: pointer;

  &:hover {
    color: var(--color-text);
    border-color: var(--color-border);
  }
`;

const selectStyle = css`
  height: 36px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 1px;
  }
`;

const visibilityRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-m);
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
`;

const visibilityTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const visibilityLabelStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const visibilityHintStyle = css`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

/**
 * The sentinel the `<select>` uses for `null`.
 *
 * `null` and `NONE` are different answers — "nobody decided" against "we decided there is no
 * badge" — and an `<option>` value is always a string, so the empty string cannot stand in for
 * either without one of them becoming the other.
 */
const INHERIT = '__inherit__';

const badgeToOption = (badge: OptInEntryBadge | null): string => badge ?? INHERIT;

const optionToBadge = (value: string): OptInEntryBadge | null => {
  const choice = OPT_IN_BADGE_CHOICES.find(entry => badgeToOption(entry.value) === value);

  return choice ? choice.value : null;
};

interface IconFieldProps {
  id: string;
  field: 'enterIcon' | 'exitIcon';
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
}

const IconField = ({ id, field, label, value, onChange }: IconFieldProps): JSX.Element => {
  const [isPicking, setIsPicking] = useState(false);

  return (
    <div className={fieldStyle}>
      <span className={monoLabelStyle}>
        {field} <span className={fieldNameStyle}>· {label}</span>
      </span>
      <div className={anchorStyle}>
        <button
          type='button'
          id={id}
          className={triggerStyle}
          aria-haspopup='dialog'
          aria-expanded={isPicking}
          onClick={() => {
            setIsPicking(current => !current);
          }}
        >
          {value === null ? (
            <span className={`${triggerTextStyle} ${inheritedStyle}`}>
              Deixar o dashboard decidir
            </span>
          ) : (
            <>
              <IconGlyph value={value} size={16} color='var(--color-text)' />
              <span className={triggerTextStyle}>{value}</span>
            </>
          )}
        </button>

        {value === null ? null : (
          <button
            type='button'
            id={`${id}-clear`}
            className={clearStyle}
            onClick={() => {
              onChange(null);
            }}
          >
            Limpar
          </button>
        )}

        {isPicking ? (
          <IconPicker
            value={value ?? ''}
            color='var(--color-text)'
            onSelect={selected => {
              onChange(selected);
            }}
            onClose={() => {
              setIsPicking(false);
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

export interface OptInEntryAppearanceFieldsProps {
  appearance: OptInAppearanceDraft;
  onChange: <K extends keyof OptInAppearanceDraft>(
    field: K,
    value: OptInAppearanceDraft[K],
  ) => void;
}

export const OptInEntryAppearanceFields = ({
  appearance,
  onChange,
}: OptInEntryAppearanceFieldsProps): JSX.Element => (
  <div id='opt-in-feature-appearance' className={wrapStyle}>
    <div className={headStyle}>
      <span className={headTitleStyle}>Aparência do botão</span>
      <p className={headHintStyle}>
        Sem idioma: o mesmo ícone e o mesmo selo valem nos três. Vazio é{' '}
        <strong>deixar o dashboard decidir</strong> — que é o padrão, e mantém a tela do cliente
        exatamente como está hoje.
      </p>
    </div>

    <div className={gridStyle}>
      <IconField
        id='opt-in-feature-enter-icon'
        field='enterIcon'
        label='Ícone · desligada'
        value={appearance.enterIcon}
        onChange={value => {
          onChange('enterIcon', value);
        }}
      />
      <IconField
        id='opt-in-feature-exit-icon'
        field='exitIcon'
        label='Ícone · ligada'
        value={appearance.exitIcon}
        onChange={value => {
          onChange('exitIcon', value);
        }}
      />

      <div className={fieldStyle}>
        <span className={monoLabelStyle}>
          enterBadge <span className={fieldNameStyle}>· Selo · desligada</span>
        </span>
        <select
          id='opt-in-feature-enter-badge'
          className={selectStyle}
          aria-label='Selo com a funcionalidade desligada'
          value={badgeToOption(appearance.enterBadge)}
          onChange={event => {
            onChange('enterBadge', optionToBadge(event.target.value));
          }}
        >
          {OPT_IN_BADGE_CHOICES.map(choice => (
            <option key={badgeToOption(choice.value)} value={badgeToOption(choice.value)}>
              {choice.label}
            </option>
          ))}
        </select>
      </div>

      <div className={fieldStyle}>
        <span className={monoLabelStyle}>
          exitBadge <span className={fieldNameStyle}>· Selo · ligada</span>
        </span>
        <select
          id='opt-in-feature-exit-badge'
          className={selectStyle}
          aria-label='Selo com a funcionalidade ligada'
          value={badgeToOption(appearance.exitBadge)}
          onChange={event => {
            onChange('exitBadge', optionToBadge(event.target.value));
          }}
        >
          {OPT_IN_BADGE_CHOICES.map(choice => (
            <option key={badgeToOption(choice.value)} value={badgeToOption(choice.value)}>
              {choice.label}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/*
      Next to the kill switch in meaning, far from it on screen, so the hint has to carry the whole
      difference by itself — it is the sentence that stops somebody reaching for this one when they
      wanted the other.
    */}
    <div className={visibilityRowStyle}>
      <Switch
        id='opt-in-feature-enter-visible'
        checked={appearance.enterVisible}
        aria-label='Convite visível na sidebar'
        onChange={checked => {
          onChange('enterVisible', checked);
        }}
      />
      <div className={visibilityTextStyle}>
        <span className={visibilityLabelStyle}>
          {appearance.enterVisible ? 'Convidando gente nova' : 'Parou de convidar gente nova'}
        </span>
        <span className={visibilityHintStyle}>
          Isto <strong>para de convidar</strong>, não desliga a funcionalidade. Desligado, quem
          ainda não entrou deixa de ver o convite —{' '}
          <strong>quem já está dentro continua dentro</strong>, com o mesmo botão levando de volta,
          que é por onde o questionário de saída coleta. Para tirar a funcionalidade de todo mundo e{' '}
          <strong>devolver o comportamento antigo</strong>, o que serve é o kill switch
          «Funcionalidade ativa», lá em cima.
        </span>
      </div>
    </div>
  </div>
);
